import React from 'react';
import styled from 'styled-components';
import dynamic from 'helpers/dynamic';
import { GetEventSubscriberInfo, UnsubscribeAll } from 'autogen/swagger/all';
import Spinner from 'modules/shared/circleSpinner';
import { hasParamByKey, deleteParamByKey } from 'modules/helpers/urlParams';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import type { ILiveEventUnsubscribe } from 'components/liveEvents';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import mediator from 'modules/mediator';
import toast from 'components/Toast';
import i18next from 'i18next';

const StyledSpinner = styled(Spinner)`
    align-items: center;
    flex: 1;
`;

export async function importBambuserLiveShopping(): Promise<void> {
    const { default: Bambuser } = await import(/* webpackChunkName: "bambuser" */ 'modules/bambuser');
    Bambuser();
}

export function initBambuserLiveShopping(): void {
    const existingScript = document.getElementById('bambuser_script');
    if (!existingScript) {
        const scriptNode = document.createElement('script');
        scriptNode.id = 'bambuser_script';
        scriptNode.type = 'text/javascript';
        scriptNode.async = true;
        scriptNode.src = 'https://lcx-embed.bambuser.com/matas/embed.js';
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode?.insertBefore(scriptNode, firstScript);
    }

    if (!window.initBambuserLiveShopping) {
        window.initBambuserLiveShopping = (item) => {
            window.initBambuserLiveShopping.queue.push(item);
        };
        window.initBambuserLiveShopping.queue = [];
    }
}

class CustomException extends Error {
    constructor(message) {
        super(message);
        this.message = message;
        this.name = 'CustomException';
    }
}

function cleanupNotifyModalParams() {
    if (hasParamByKey('OpenSubscriberModal')) deleteParamByKey('OpenSubscriberModal');
    if (hasParamByKey('ExternalLinkLiveEventId')) deleteParamByKey('ExternalLinkLiveEventId');
}

function cleanupUnsubscribeModalParam() {
    if (hasParamByKey('UnsubscribeGuid')) deleteParamByKey('UnsubscribeGuid');
}

export async function openNotifyModal(url: string): Promise<void> {
    const { searchParams } = new URL(url, window.location.origin);
    const param = searchParams?.has('id') ? searchParams.get('id') : null;
    const id = param ? Number.parseInt(param) : null;
    const filterIds = searchParams.getAll('filterids')?.map((filterId: string) => Number.parseInt(filterId));

    let modal;

    function closeModal() {
        modal({ open: false });
    }

    async function openSuccessModal(title: string, description: string) {
        const NotifyDialog = dynamic(
            () => import(/* webpackChunkName: "notifyDialog" */ 'components/live/notifyDialog')
        );
        await NotifyDialog;

        modal({
            layout: { theme: { maxWidth: '400px', minWidth: 'auto' } },
            content: <NotifyDialog title={title} description={description} />
        });
    }

    try {
        const { default: createModal } = await import(/* webpackChunkName: "modal" */ 'modules/modal');
        const { default: NotifyHeader } = await import(/* webpackChunkName: "notifyHeader" */ './notifyHeader');

        modal = createModal({
            layout: {
                customTheme: { width: '100%', maxWidth: '800px', minHeight: '200px' }
            },
            onAfterClose: () => cleanupNotifyModalParams()
        });

        modal({ open: true, content: <StyledSpinner size={50} strokeWidth={3} timeout /> });

        let model;
        if (id) {
            model = await GetEventSubscriberInfo({ id });
        } else {
            model = await GetEventSubscriberInfo({ filterIds });
        }
        const NotifyForm = dynamic(() => import(/* webpackChunkName: "notifyForm" */ 'components/live/notifyForm'));
        await NotifyForm;

        UAEventTracking({
            eventCategory: 'liveEvent',
            eventAction: 'openNotifyModal'
        });

        modal({
            layout: {
                theme: { width: '100%', maxWidth: '800px' },
                header: <NotifyHeader close={() => modal({ open: false })} />
            },
            content: <NotifyForm model={model} close={closeModal} onSuccess={openSuccessModal} />
        });
    } catch (error) {
        console.error(error);
    }
}

export async function openUnsubscribeModal(model: ILiveEventUnsubscribe): Promise<any> {
    const { title, description, confirmCta, unsubscribedSuccesful, unsubscribeGuid } = model;

    let modal;

    async function unsubscribeAll() {
        try {
            const response = await UnsubscribeAll({ secret: unsubscribeGuid });

            const { success, message } = response;

            if (success) {
                UAEventTracking({
                    eventCategory: 'liveEvent',
                    eventAction: 'unsubAllNotifications'
                });
                modal({ open: false });
                mediator.publish('growl', {
                    message: unsubscribedSuccesful,
                    remove: 3000
                });
            } else if (message) {
                throw new CustomException(message);
            } else {
                throw new Error('unknown error');
            }
        } catch (error: any) {
            console.error(error);

            if (error instanceof CustomException) {
                // display error message
                mediator.publish('growl', {
                    message: error.message,
                    remove: 5000
                });
            } else {
                // display generic error
                toast.error(i18next.t(GeneralExceptionError));
            }
        }
    }

    try {
        const { default: createModal } = await import(/* webpackChunkName: "modal" */ 'modules/modal');

        modal = createModal({
            layout: { customTheme: { maxWidth: '400px', minWidth: 'auto', minHeight: '200px' } },
            onAfterClose: () => cleanupUnsubscribeModalParam()
        });

        modal({ open: true, content: <StyledSpinner size={50} strokeWidth={3} timeout /> });

        const NotifyDialog = dynamic(
            () => import(/* webpackChunkName: "notifyDialog" */ 'components/live/notifyDialog')
        );
        await NotifyDialog;

        modal({
            content: (
                <NotifyDialog
                    title={title}
                    description={description}
                    confirmText={confirmCta}
                    confirmCallback={unsubscribeAll}
                />
            )
        });
    } catch (error) {
        console.error(error);
    }
}
